import React, { useState, useEffect } from 'react';
import {
  withTextLocalizer,
  selectFrom,
  selectBackby,
  setFrom,
  setBackBy,
  clearFilter,
  selectMapCenterLocation,
  selectMapZoom,
  selectBrandSettings,
} from 'getaway-data-layer';
import { useSelector, useDispatch } from 'react-redux';
import { addMilliseconds, differenceInHours, isBefore } from 'date-fns';
import MapFilterView from './EmbeddedCardView';
import {
  initialReturnTime,
  minimumReturnTime,
  minimumStartTime,
} from '../../../../utils';
import moment from 'moment/moment';

type Props = { localize: any };

export const isSafari =
  ['iPhone', 'iPad', 'iPod']?.includes(
    // @ts-ignore
    window?.navigator?.userAgentData?.platform || window?.navigator?.platform
  ) || /^((?!chrome|android).)*safari/i.test(window?.navigator?.userAgent);

const EmbeddedCard = ({ localize }: Props) => {
  const dispatch = useDispatch();
  const from = useSelector(selectFrom) as any;
  const backBy = useSelector(selectBackby) as any;
  const mapCenterLocation = useSelector(selectMapCenterLocation);
  const zoom = useSelector(selectMapZoom);

  const startDate = from ? new Date(from) : minimumStartTime();
  const backByDate = backBy ? new Date(backBy) : initialReturnTime(startDate);

  const [selectedStartDate, setSelectedStartDate] = useState(startDate) as any;
  const [selectedEndDate, setSelectedEndDate] = useState(backByDate) as any;
  const [customError, setCustomError] = useState('') as any;

  const bookingDuration = useSelector(selectBrandSettings)?.bookingDuration;
  const bookingDurationAsMilliseconds = moment
    .duration(bookingDuration)
    .asMilliseconds();
  const maximumDate = bookingDuration
    ? addMilliseconds(
        new Date(selectedStartDate),
        bookingDurationAsMilliseconds
      )
    : undefined;

  const onChangeStartDate = (e: any) => {
    setSelectedStartDate(e);
    if (isBefore(selectedEndDate, e)) {
      setSelectedEndDate(initialReturnTime(e));
      // dispatch(setBackBy(initialReturnTime(e)));
    }
  };

  const onChangeEndDate = (e: any) => {
    setSelectedEndDate(e);
  };

  const onReset = () => {
    dispatch(clearFilter());
    setSelectedStartDate(minimumStartTime());
    setSelectedEndDate(initialReturnTime(minimumStartTime()));
  };

  const onPressFilter = () => {
    const currentUrl = new URL(window?.location?.href);
    const url = new URL(`${window?.location?.origin}/map`);

    url.searchParams.append('from', selectedStartDate.toISOString());
    url.searchParams.append('backBy', selectedEndDate.toISOString());

    if (mapCenterLocation?.latitude && mapCenterLocation?.longitude) {
      url.searchParams.append('lat', mapCenterLocation.latitude.toString());
      url.searchParams.append('lng', mapCenterLocation.longitude.toString());
    }

    if (zoom) {
      url.searchParams.append('zoom', zoom.toString());
    }

    // Preserve existing UTM parameters if they exist
    const utmSource = currentUrl?.searchParams?.get('utm_source');
    const utmMedium = currentUrl?.searchParams?.get('utm_medium');

    if (utmSource) {
      url.searchParams.append('utm_source', utmSource);
    }

    if (utmMedium) {
      url.searchParams.append('utm_medium', utmMedium);
    }

    window.open(url.toString(), '_blank');
  };

  const validateDates = () => {
    // Validation checks
    if (isBefore(selectedStartDate, new Date())) {
      setCustomError(localize('wj.map.filter.violation.min'));
    } else if (isBefore(selectedEndDate, selectedStartDate)) {
      setCustomError(localize('wj.map.filter.violation.order'));
    } else if (differenceInHours(selectedEndDate, selectedStartDate) < 1) {
      setCustomError(localize('wj.map.filter.violation.duration'));
    } else {
      setCustomError('');
      dispatch(setFrom(selectedStartDate.toUTCString()));
      dispatch(setBackBy(selectedEndDate.toUTCString()));
    }
  };

  useEffect(() => {
    validateDates();
  }, [selectedStartDate, selectedEndDate]);

  return (
    <MapFilterView
      localize={localize}
      selectedStartDate={selectedStartDate}
      onChangeStartDate={onChangeStartDate}
      selectedEndDate={selectedEndDate}
      onChangeEndDate={onChangeEndDate}
      minStart={new Date()}
      minEnd={minimumReturnTime(selectedStartDate)}
      maxEnd={maximumDate}
      onReset={onReset}
      onPressFilter={onPressFilter}
      customError={customError}
    />
  );
};

export default withTextLocalizer(EmbeddedCard);
